import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { MediaFilterType, ModelResource, SliderType } from '../types/enums';
import { MediaResponseInterface } from '../types/media.interface';
import {
  catchError,
  map,
  Observable,
  of,
  retry,
  switchMap,
  throwError,
} from 'rxjs';
import { HttpBaseResponse } from '../types/http-response.interface';
import { SliderResponseInterface } from '../types/slider.interface';
import { ProductInterface } from '../types/product.interface';
import { ContactFormInterface, FormInterface } from '../types/form.interface';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  constructor(private http: HttpClient) {}

  transformString(text: string): string {
    return text.trim().replace(/\s+/g, '-').toLowerCase();
  }

  /**
   *
   * Funzione per recuperare i media files per id del prodotto/servizio.
   *
   * @returns
   */
  getMedia(
    resourceId: number,
    resourceModel: ModelResource,
    featured?: boolean,
    mediaFilterTypeId?: MediaFilterType,
    dateStart?: boolean
  ) {
    return this.http
      .post<MediaResponseInterface[]>(
        `${environment.urlApi}media/files/listFilter`,
        {
          resourceId: resourceId,
          resourceModel: resourceModel,
          featured: featured,
          mediaFilterTypeId: mediaFilterTypeId,
          dateStart: dateStart,
        }
      )
      .pipe(
        retry(2),
        catchError((error) => of([]))
      );
  }

  /**
   *
   * Funzione per recuperare la galleria per news, ricette e slider home
   *
   * @returns
   */
  getGallery(
    filterResourceId: number | undefined,
    filterTypeId: SliderType
  ): Observable<string[]> {
    const body = {
      filterResourceId,
      filterTypeId,
      filterStatus: 'active',
    };
    return this.http
      .post<HttpBaseResponse<SliderResponseInterface>>(
        `${environment.urlApi}slider/sliders/list`,
        body
      )
      .pipe(
        retry(2),
        switchMap((res: HttpBaseResponse<SliderResponseInterface>) => {
          if (!res?.items || res?.items?.length === 0) {
            // Se non ci sono slider, ritorna un array vuoto

            return of([]);
          }
          const slider = res.items[0];
          // Recupera i media files per lo slider
          return this.getMedia(slider.id, ModelResource.Slider).pipe(
            map((media: MediaResponseInterface[]) => {
              return media.map((m) => m?.MediaFile?.urlSigned || '');
            }),
            catchError((error) => {
              console.error('Error fetching media:', error);
              return throwError(() => new Error('Failed to fetch media'));
            })
          );
        })
      );
  }

  getMainSlider(
    filterResourceId: number | undefined,
    filterTypeId: SliderType,
    filterAgencyId?: number
  ) {
    const body = {
      filterResourceId,
      filterTypeId,
      filterStatus: 'active',
      filterAgencyId: filterAgencyId,
    };
    return this.http
      .post<HttpBaseResponse<SliderResponseInterface>>(
        `${environment.urlApi}slider/sliders/list`,
        body
      )
      .pipe(
        retry(2),
        switchMap((res: HttpBaseResponse<SliderResponseInterface>) => {
          if (!res?.items || res?.items?.length === 0) {
            // Se non ci sono slider, ritorna un array vuoto

            return of([]);
          }
          const slider = res.items[0];
          // Recupera i media files per lo slider
          return this.getMedia(
            slider.id,
            ModelResource.Slider,
            undefined,
            undefined,
            true
          ).pipe(
            map((mediaFiles: MediaResponseInterface[]) =>
              mediaFiles.filter((mediaFile) => {
                const now = new Date();
                const startDate =
                  mediaFile?.Dates?.dateStart?.split('T')[0] || null;
                const endDate =
                  mediaFile?.Dates?.dateEnd?.split('T')[0] || null;

                const startDateObj = startDate ? new Date(startDate) : null;
                const endDateObj = endDate ? new Date(endDate) : null;

                // Se non c'è nessuna data mostro sempre
                if (!startDateObj && !endDateObj) {
                  return true;
                }

                // Se c'è solo la data di inizio e non c'è la data di fine
                // mostro solo se la data di inizio è minore o uguale alla data corrente
                if (startDateObj && !endDateObj) {
                  return startDateObj <= now;
                }

                // Se c'è solo la data di fine e non c'è la data di inizio
                // mostro solo se la data di fine è maggiore o uguale alla data corrente
                if (!startDateObj && endDateObj) {
                  return now <= endDateObj;
                }

                // Se c'è sia la data di inizio che la data di fine
                return startDateObj! <= now && now <= endDateObj!;
              })
            ),
            catchError((error) => {
              console.error('Error fetching media:', error);
              return throwError(() => new Error('Failed to fetch media'));
            })
          );
        })
      );
  }

  /**
   *
   * Funzione pubblica per recuperare la lista prodotti della piattaforma.
   *
   * @returns
   */
  getProducts(
    body: any
  ): Observable<HttpBaseResponse<ProductInterface> | null> {
    return this.http
      .post<HttpBaseResponse<ProductInterface>>(
        `${environment.urlApi}product/products/list`,
        body
      )
      .pipe(
        retry(2),
        catchError((error) => of(null))
      );
  }

  getProduct(id: string) {
    return this.http
      .get<ProductInterface>(`${environment.urlApi}product/products/get/${id}`)
      .pipe(
        retry(2),
        catchError((error) => of(null))
      );
  }

  getProductByExternalId(extrernalId: string) {
    return this.http
      .post<ProductInterface>(`${environment.urlApi}product/products/get`, {
        search: extrernalId,
        field: 'externalId',
      })
      .pipe(
        retry(2),
        catchError((error) => of(null))
      );
  }

  saveForm(body: FormInterface) {
    return this.http.post(`${environment.urlApi}ard/homepage/form`, body);
  }

  saveContact(contactData: ContactFormInterface) {
    return this.http.post(`${environment.urlApi}ard/homepage/contactsWeb`, contactData);
  }
}
